import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Upload, Sparkles, Save, Eye, Send, Plus, Trash2, FileUp, History, RotateCcw, Megaphone } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/apiClient";
import { AdminHeader, AdminPage } from "@/components/admin/AdminUI";
import AIStepper from "@/components/admin/AIStepper";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUSES = ["Filed", "Pending", "Notice Issued", "Under Consideration", "Interim Directions Issued",
  "Relief Granted", "Partly Allowed", "Disposed", "Decided", "Closed"];

const EMPTY = { title: "", court: "", bench: "", case_number: "", pil_code: "", petitioner: "",
  respondents: [], cause_of_action: "", background: "", principal_issue: "", reliefs_sought: [],
  decision_status: "", status: "Pending", category: "", filing_date: "", tags: [],
  statutory_provisions: [], state: "draft", seo_title: "", meta_description: "", featured: false, ai_generated: false };

function Field({ label, children, hint }) {
  return (
    <div className="space-y-1.5">
      <label className="label-eyebrow text-slate-500 block">{label}</label>
      {children}
      {hint && <p className="text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

async function runStepper(setStage, promise) {
  setStage("uploading");
  const timers = [];
  timers.push(setTimeout(() => setStage("extracting"), 700));
  timers.push(setTimeout(() => setStage("analysing"), 1600));
  timers.push(setTimeout(() => setStage("generating"), 3000));
  try {
    const res = await promise;
    timers.forEach(clearTimeout);
    setStage("ready");
    return res;
  } catch (e) {
    timers.forEach(clearTimeout);
    throw e;
  }
}

export default function PilEditor() {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [aiStage, setAiStage] = useState(null);
  const [aiError, setAiError] = useState("");
  const [aiReviewed, setAiReviewed] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [orders, setOrders] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const petitionRef = useRef();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setList = (k, v) => set(k, v.split("\n").map((x) => x.trim()).filter(Boolean));

  useEffect(() => { api.get("/categories").then(({ data }) => setCats(data)).catch(() => {}); }, []);
  useEffect(() => {
    if (isNew) return;
    api.get(`/pils/${id}`).then(({ data }) => {
      setForm({ ...EMPTY, ...data.pil });
      setOrders(data.orders || []); setTimeline(data.timeline || []);
    }).catch(() => toast.error("Failed to load PIL")).finally(() => setLoading(false));
  }, [id, isNew]);

  const uploadPetition = async (file) => {
    if (!file) return;
    setAiError(""); setAiStage("uploading");
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await runStepper(setAiStage, api.post("/ai/analyse-petition", fd));
      const a = data.analysis || {};
      if (a._ai_error) { setAiError(a._ai_error); return; }
      setForm((f) => ({
        ...f,
        title: a.title || f.title,
        court: a.court || f.court,
        bench: a.bench || f.bench,
        case_number: a.case_number || f.case_number,
        pil_code: f.pil_code || a.pil_code_suggestion || "",
        petitioner: a.petitioner || f.petitioner,
        respondents: a.respondents || f.respondents,
        cause_of_action: a.cause_of_action || f.cause_of_action,
        background: a.background || f.background,
        principal_issue: a.principal_issue || f.principal_issue,
        reliefs_sought: a.reliefs_sought || f.reliefs_sought,
        decision_status: a.present_status || f.decision_status,
        status: a.present_status && STATUSES.includes(a.present_status) ? a.present_status : (a.status || f.status),
        category: a.category || f.category,
        filing_date: a.filing_date || f.filing_date,
        tags: a.tags || f.tags,
        statutory_provisions: a.statutory_provisions || f.statutory_provisions,
        source_file_id: data.file?.id,
        ai_generated: true,
      }));
      setAiReviewed(false);
      toast.success("AI analysis complete — review all fields before publishing");
    } catch (e) {
      setAiError(e.response?.data?.detail || "AI processing failed. Please try again.");
    }
  };

  const save = async (state) => {
    if (!form.title) return toast.error("Title is required");
    setSaving(true);
    const payload = { ...form, state };
    try {
      let saved;
      if (isNew) { const { data } = await api.post("/pils", payload); saved = data; }
      else { const { data } = await api.put(`/pils/${id}`, payload); saved = data; }
      toast.success(state === "published" ? "PIL published" : "Saved");
      if (isNew) navigate(`/admin/pils/${saved.id}`);
      else setForm({ ...EMPTY, ...saved });
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-8 text-slate-400 animate-pulse">Loading…</div>;

  return (
    <>
      <AdminHeader title={isNew ? "Add New PIL" : "Edit PIL"} subtitle={form.pil_code || "New matter"} actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild><Link to="/admin/pils"><ArrowLeft size={15} className="mr-1.5" /> Back</Link></Button>
          {form.state === "published" && !isNew && <Button variant="outline" asChild><a href={`/pil/${form.pil_code?.toLowerCase()}`} target="_blank" rel="noopener noreferrer"><Eye size={15} className="mr-1.5" /> Preview</a></Button>}
          {!isNew && <Button variant="outline" onClick={() => setShowVersions(true)} data-testid="version-history-btn"><History size={15} className="mr-1.5" /> History</Button>}
          <Button variant="outline" onClick={() => save("draft")} disabled={saving} data-testid="save-draft-btn"><Save size={15} className="mr-1.5" /> Save Draft</Button>
          <Button onClick={() => save("published")} disabled={saving} className="bg-npst-navy hover:bg-npst-navyDeep text-white" data-testid="publish-pil-btn"><Send size={15} className="mr-1.5" /> Publish</Button>
        </div>
      } />
      <AdminPage>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* AI upload for new PIL */}
            {isNew && (
              <div className="bg-white border border-slate-200 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-3"><Sparkles size={18} className="text-npst-gold" /><h3 className="font-serif text-xl text-npst-ink">Upload Petition — AI Extraction</h3></div>
                <p className="text-sm text-slate-500 mb-4">Upload the complete petition (PDF, DOC, DOCX). The system extracts and drafts the fields for your review. Nothing is published automatically.</p>
                {!aiStage && (
                  <button onClick={() => petitionRef.current?.click()} data-testid="upload-petition-btn" className="w-full border-2 border-dashed border-slate-300 rounded-lg py-10 hover:border-npst-gold hover:bg-npst-goldLight/40 transition-colors flex flex-col items-center gap-2 text-slate-500">
                    <Upload size={28} /><span className="text-sm font-medium">Click to upload petition</span><span className="text-xs">PDF · DOC · DOCX (max 25MB)</span>
                  </button>
                )}
                <input ref={petitionRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => uploadPetition(e.target.files[0])} />
                {aiStage && <div className="mt-2"><AIStepper stage={aiStage} error={aiError} onRetry={() => { setAiStage(null); setAiError(""); }} /></div>}
              </div>
            )}

            {form.ai_generated && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-3 flex items-center justify-between" data-testid="ai-review-banner">
                <span className="text-sm font-semibold text-purple-800 uppercase tracking-wider">AI Generated — Review Required</span>
                <label className="flex items-center gap-2 text-sm text-purple-800"><input type="checkbox" checked={aiReviewed} onChange={(e) => setAiReviewed(e.target.checked)} /> Reviewed</label>
              </div>
            )}

            <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
              <Field label="PIL Title"><Textarea data-testid="pil-title-input" rows={2} value={form.title} onChange={(e) => set("title", e.target.value)} /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Court"><Input value={form.court} onChange={(e) => set("court", e.target.value)} data-testid="pil-court-input" /></Field>
                <Field label="Bench / Judges"><Input value={form.bench} onChange={(e) => set("bench", e.target.value)} /></Field>
                <Field label="Case Number"><Input value={form.case_number} onChange={(e) => set("case_number", e.target.value)} data-testid="pil-case-number-input" /></Field>
                <Field label="PIL Code" hint="Auto-suggested; you may override before publishing"><Input value={form.pil_code} onChange={(e) => set("pil_code", e.target.value)} data-testid="pil-code-input" className="font-mono" /></Field>
                <Field label="Filing Date"><Input value={form.filing_date} onChange={(e) => set("filing_date", e.target.value)} placeholder="e.g. 28 July 2026" /></Field>
                <Field label="Petitioner"><Input value={form.petitioner} onChange={(e) => set("petitioner", e.target.value)} /></Field>
              </div>
              <Field label="Respondents (one per line)"><Textarea rows={3} value={(form.respondents || []).join("\n")} onChange={(e) => setList("respondents", e.target.value)} /></Field>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
              <Field label="Cause of Action (public-facing)"><Textarea data-testid="pil-cause-input" rows={5} value={form.cause_of_action} onChange={(e) => set("cause_of_action", e.target.value)} /></Field>
              <Field label="Decision / Present Status"><Textarea data-testid="pil-decision-input" rows={4} value={form.decision_status} onChange={(e) => set("decision_status", e.target.value)} /></Field>
              <Field label="Case Overview / Background"><Textarea rows={4} value={form.background} onChange={(e) => set("background", e.target.value)} /></Field>
              <Field label="Principal Issue"><Textarea rows={2} value={form.principal_issue} onChange={(e) => set("principal_issue", e.target.value)} /></Field>
              <Field label="Reliefs Sought (one per line)"><Textarea rows={4} value={(form.reliefs_sought || []).join("\n")} onChange={(e) => setList("reliefs_sought", e.target.value)} /></Field>
            </div>

            {/* Orders + timeline for existing PIL */}
            {!isNew && <OrdersPanel pilId={id} pilStatus={form.status} orders={orders} timeline={timeline} onChange={() => api.get(`/pils/${id}`).then(({ data }) => { setForm((f) => ({ ...f, status: data.pil.status, decision_status: data.pil.decision_status })); setOrders(data.orders); setTimeline(data.timeline); })} />}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
              <Field label="Status"><Select value={form.status} onValueChange={(v) => set("status", v)}><SelectTrigger data-testid="pil-status-select"><SelectValue /></SelectTrigger><SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></Field>
              <Field label="Category"><Select value={form.category || "__none"} onValueChange={(v) => set("category", v === "__none" ? "" : v)}><SelectTrigger data-testid="pil-category-select"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent><SelectItem value="__none">None</SelectItem>{cats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent></Select></Field>
              <Field label="Feature on Homepage"><label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.featured} onChange={(e) => set("featured", e.target.checked)} /> Featured</label></Field>
              <Field label="Tags (one per line)"><Textarea rows={3} value={(form.tags || []).join("\n")} onChange={(e) => setList("tags", e.target.value)} /></Field>
              <Field label="Statutory Provisions (one per line)"><Textarea rows={3} value={(form.statutory_provisions || []).join("\n")} onChange={(e) => setList("statutory_provisions", e.target.value)} /></Field>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5">
              <div className="label-eyebrow text-npst-gold">SEO</div>
              <Field label="SEO Title"><Input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} /></Field>
              <Field label="Meta Description"><Textarea rows={3} value={form.meta_description} onChange={(e) => set("meta_description", e.target.value)} /></Field>
            </div>
          </div>
        </div>
      </AdminPage>
      {showVersions && <VersionsDialog pilId={id} onClose={() => setShowVersions(false)} onRestore={(p) => { setForm({ ...EMPTY, ...p }); setShowVersions(false); }} />}
    </>
  );
}

function VersionsDialog({ pilId, onClose, onRestore }) {
  const [versions, setVersions] = useState(null);
  useEffect(() => { api.get(`/pils/${pilId}/versions`).then(({ data }) => setVersions(data)).catch(() => setVersions([])); }, [pilId]);
  const restore = async (v) => {
    if (!window.confirm("Restore this version? Current content is saved to history first.")) return;
    try { const { data } = await api.post(`/pils/${pilId}/restore-version/${v.id}`); toast.success("Version restored"); onRestore(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Version History</DialogTitle></DialogHeader>
        {!versions ? <p className="text-slate-400 text-sm">Loading…</p>
          : versions.length === 0 ? <p className="text-slate-400 text-sm">No previous versions yet. Versions are saved whenever the cause of action, decision/status or background changes.</p>
          : (
            <div className="space-y-3">
              {versions.map((v) => (
                <div key={v.id} className="border border-slate-200 rounded-lg p-4" data-testid="version-item">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs text-slate-500 font-mono">{v.edited_at?.replace("T", " ").slice(0, 16)} · {v.edited_by}</div>
                    <Button size="sm" variant="outline" onClick={() => restore(v)} data-testid="restore-version-btn"><RotateCcw size={13} className="mr-1.5" /> Restore</Button>
                  </div>
                  {v.status && <div className="text-xs mb-1"><span className="text-slate-400 uppercase">Status:</span> {v.status}</div>}
                  {v.decision_status && <p className="text-sm text-slate-600 line-clamp-2"><span className="text-slate-400 uppercase text-xs">Decision:</span> {v.decision_status}</p>}
                  {v.cause_of_action && <p className="text-sm text-slate-600 line-clamp-2 mt-1"><span className="text-slate-400 uppercase text-xs">Cause:</span> {v.cause_of_action}</p>}
                </div>
              ))}
            </div>
          )}
      </DialogContent>
    </Dialog>
  );
}

function OrdersPanel({ pilId, pilStatus, orders, timeline, onChange }) {
  const [open, setOpen] = useState(false);
  const [tlOpen, setTlOpen] = useState(false);
  const navigate = useNavigate();

  const delOrder = async (o) => { if (!window.confirm("Delete this order?")) return; await api.delete(`/orders/${o.id}`); toast.success("Order deleted"); onChange(); };
  const delTl = async (t) => { if (!window.confirm("Delete event?")) return; await api.delete(`/timeline/${t.id}`); onChange(); };
  const genPress = async (o) => {
    toast.info("Drafting press release…");
    try {
      const { data: draft } = await api.post(`/orders/${o.id}/generate-press`);
      const payload = { headline: draft.headline || "", subheadline: draft.subheadline || "", excerpt: draft.excerpt || "",
        body: draft.body || "", category: draft.category || "Press Release", tags: draft.tags || [], seo_title: draft.seo_title || "",
        meta_description: draft.meta_description || "", social_excerpt: draft.social_excerpt || "", related_pils: draft.related_pils || [], state: "draft" };
      const { data: pr } = await api.post("/press", payload);
      toast.success("Draft press release created");
      navigate(`/admin/press/${pr.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to generate press release"); }
  };

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-xl text-npst-ink">Orders & Case Documents</h3>
          <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="upload-order-btn"><FileUp size={14} className="mr-1.5" /> Upload New Order</Button>
        </div>
        {orders.length === 0 ? <p className="text-sm text-slate-400">No orders uploaded yet.</p> : (
          <div className="space-y-2">
            {orders.map((o) => (
              <div key={o.id} className="flex items-center justify-between py-2 border-b border-slate-100 text-sm">
                <div><span className="font-medium text-npst-ink">{o.title}</span> <span className="text-slate-400">· {o.order_date} · {o.category}</span></div>
                <div className="flex items-center gap-3">
                  <button onClick={() => genPress(o)} className="inline-flex items-center gap-1 text-npst-navy hover:text-npst-gold text-xs font-semibold uppercase tracking-wider" data-testid={`gen-press-${o.id}`}><Megaphone size={14} /> Press</button>
                  <button onClick={() => delOrder(o)} className="text-slate-400 hover:text-red-600"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-xl text-npst-ink">Case Timeline</h3>
          <Button size="sm" variant="outline" onClick={() => setTlOpen(true)} data-testid="add-timeline-btn"><Plus size={14} className="mr-1.5" /> Add Event</Button>
        </div>
        {timeline.length === 0 ? <p className="text-sm text-slate-400">No timeline events yet.</p> : (
          <div className="space-y-2">
            {timeline.map((t) => (
              <div key={t.id} className="flex items-center justify-between py-2 border-b border-slate-100 text-sm">
                <div><span className="font-mono text-xs text-npst-gold">{t.event_date}</span> <span className="text-npst-ink ml-2">{t.title}</span>{t.ai_generated && <span className="ml-2 text-[9px] bg-purple-50 text-purple-700 px-1 rounded uppercase">AI</span>}</div>
                <button onClick={() => delTl(t)} className="text-slate-400 hover:text-red-600"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {open && <OrderUploadDialog pilId={pilId} pilStatus={pilStatus} onClose={() => setOpen(false)} onDone={() => { setOpen(false); onChange(); }} />}
      {tlOpen && <TimelineDialog pilId={pilId} onClose={() => setTlOpen(false)} onDone={() => { setTlOpen(false); onChange(); }} />}
    </>
  );
}

function OrderUploadDialog({ pilId, pilStatus, onClose, onDone }) {
  const fileRef = useRef();
  const [stage, setStage] = useState(null);
  const [error, setError] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [fileId, setFileId] = useState(null);
  const [f, setF] = useState({ title: "", category: "Interim Order", order_date: "", court: "", description: "", is_public: true, apply_status: "", apply_decision_status: "", timeline_title: "", timeline_description: "", timeline_date: "" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const analyse = async (file) => {
    if (!file) return;
    setError(""); setStage("uploading");
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await runStepper(setStage, api.post(`/pils/${pilId}/ai-analyse-order`, fd));
      const a = data.analysis || {};
      setFileId(data.file?.id);
      if (a._ai_error) { setError(a._ai_error); }
      setAnalysis(a);
      setF((p) => ({ ...p,
        title: a.nature_of_order || "Court Order",
        category: a.nature_of_order || "Order",
        order_date: a.order_date || "",
        court: a.court || "",
        description: (a.key_directions || []).join("; "),
        apply_status: a.suggested_status && a.suggested_status !== pilStatus ? a.suggested_status : "",
        apply_decision_status: a.suggested_decision_status || "",
        timeline_title: a.suggested_timeline_title || "",
        timeline_description: a.suggested_timeline_description || "",
        timeline_date: a.order_date || "",
      }));
    } catch (e) { setError(e.response?.data?.detail || "AI processing failed"); }
  };

  const saveOrder = async (withUpdates) => {
    if (!fileId) return toast.error("Upload an order file first");
    const payload = { pil_id: pilId, file_id: fileId, title: f.title, category: f.category, order_date: f.order_date, court: f.court, description: f.description, is_public: f.is_public };
    if (withUpdates) {
      if (f.apply_status) payload.apply_status = f.apply_status;
      if (f.apply_decision_status) payload.apply_decision_status = f.apply_decision_status;
      if (f.timeline_title) { payload.timeline_title = f.timeline_title; payload.timeline_description = f.timeline_description; payload.timeline_date = f.timeline_date; }
    }
    try { await api.post("/orders", payload); toast.success(withUpdates ? "Order saved & PIL updated" : "Order saved"); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Upload New Court Order</DialogTitle></DialogHeader>
        {!stage && (
          <button onClick={() => fileRef.current?.click()} className="w-full border-2 border-dashed border-slate-300 rounded-lg py-10 hover:border-npst-gold flex flex-col items-center gap-2 text-slate-500" data-testid="order-upload-zone">
            <Upload size={26} /><span className="text-sm">Upload order (PDF · DOC · DOCX)</span>
          </button>
        )}
        <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => analyse(e.target.files[0])} />
        {stage && stage !== "ready" && <AIStepper stage={stage} error={error} onRetry={() => setStage(null)} />}
        {analysis && stage === "ready" && (
          <div className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <div className="label-eyebrow text-npst-gold mb-3">Proposed Update to PIL</div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-400 text-xs uppercase">Old Status</span><div className="font-medium">{pilStatus}</div></div>
                <div><span className="text-slate-400 text-xs uppercase">Suggested Status</span><div className="font-medium text-npst-navy">{analysis.suggested_status || "—"}</div></div>
              </div>
              {analysis.key_directions?.length > 0 && <div className="mt-3"><span className="text-slate-400 text-xs uppercase">Key Directions</span><ul className="list-disc pl-5 text-sm mt-1">{analysis.key_directions.map((k, i) => <li key={i}>{k}</li>)}</ul></div>}
              {analysis.relief_granted && <div className="mt-2 text-sm"><span className="text-slate-400 text-xs uppercase">Relief Recorded</span><div>{analysis.relief_granted}</div></div>}
              <p className="text-xs text-purple-700 mt-3 uppercase tracking-wider font-semibold">AI Generated — Review before applying. AI never infers relief not present in the order.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Order Title"><Input value={f.title} onChange={(e) => set("title", e.target.value)} /></Field>
              <Field label="Order Date"><Input value={f.order_date} onChange={(e) => set("order_date", e.target.value)} /></Field>
              <Field label="Category"><Input value={f.category} onChange={(e) => set("category", e.target.value)} /></Field>
              <Field label="Court"><Input value={f.court} onChange={(e) => set("court", e.target.value)} /></Field>
            </div>
            <Field label="Apply Status Update"><Select value={f.apply_status || "__none"} onValueChange={(v) => set("apply_status", v === "__none" ? "" : v)}><SelectTrigger><SelectValue placeholder="Do not change" /></SelectTrigger><SelectContent><SelectItem value="__none">Do not change</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Apply Decision / Present Status"><Textarea rows={3} value={f.apply_decision_status} onChange={(e) => set("apply_decision_status", e.target.value)} /></Field>
            <Field label="Suggested Timeline Event"><Input value={f.timeline_title} onChange={(e) => set("timeline_title", e.target.value)} placeholder="Leave blank to skip" /></Field>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.is_public} onChange={(e) => set("is_public", e.target.checked)} /> Publicly downloadable</label>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => saveOrder(false)} data-testid="save-order-only">Save Document Only</Button>
              <Button onClick={() => saveOrder(true)} className="bg-npst-navy text-white" data-testid="approve-update-btn">Approve & Update</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TimelineDialog({ pilId, onClose, onDone }) {
  const [f, setF] = useState({ title: "", description: "", event_date: "" });
  const save = async () => {
    if (!f.title || !f.event_date) return toast.error("Title and date required");
    try { await api.post("/timeline", { pil_id: pilId, ...f, approved: true }); toast.success("Event added"); onDone(); }
    catch { toast.error("Failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent><DialogHeader><DialogTitle className="font-serif text-2xl">Add Timeline Event</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Date"><Input value={f.event_date} onChange={(e) => setF((p) => ({ ...p, event_date: e.target.value }))} placeholder="e.g. 18 SEP 2026" data-testid="tl-date" /></Field>
          <Field label="Title"><Input value={f.title} onChange={(e) => setF((p) => ({ ...p, title: e.target.value }))} data-testid="tl-title" /></Field>
          <Field label="Description"><Textarea rows={3} value={f.description} onChange={(e) => setF((p) => ({ ...p, description: e.target.value }))} /></Field>
          <div className="flex justify-end"><Button onClick={save} className="bg-npst-navy text-white" data-testid="save-tl-btn">Add Event</Button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
